'use client';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { CalendarIcon, CircleDashed, GitBranch, TrendingUp, Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StepTypeBadge } from './StepTypeBadge';
import { Funnel } from '@/lib/api/funnels';
import { formatDate } from '@/lib/format';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

export type FunnelTableColumnId =
  | 'select'
  | 'funnels.id'
  | 'funnels.name'
  | 'funnels.status'
  | 'funnels.isDefault'
  | 'funnels.stepsCount'
  | 'funnels.updatedAt'
  | 'funnels.createdAt'
  | 'actions';

export type FunnelTableSortableColumnId = Exclude<FunnelTableColumnId, 'select' | 'actions'>;

interface GetFunnelsTableColumnsProps {
  /** Reserved: kept in the signature for parity with other store-scoped table
   *  column builders and future cross-links, even though the ID column now
   *  uses `CopyableResourceBadge` (no per-store link needed). */
  storeId?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  onEditFunnel?: (funnelId: string) => void;
  onDeleteFunnel?: (funnelId: string) => void;
  onSetAsDefault?: (funnelId: string) => void;
  onExportFunnel?: (funnelId: string) => void;
}

const columnHelper = createColumnHelper<Funnel>();

export function getFunnelsTableColumns({
  t,
  onEditFunnel,
  onDeleteFunnel,
  onSetAsDefault,
  onExportFunnel,
}: GetFunnelsTableColumnsProps) {
  return [
    // 1. Select checkbox
    columnHelper.display({
      id: 'select' satisfies FunnelTableColumnId,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('funnelsTable.columns.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    }),

    // 2. Funnel Name (primary column)
    columnHelper.accessor('name', {
      id: 'funnels.name' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.funnelName')} />
      ),
      cell: ({ row }) => {
        const { storeRoutes } = useStoreNavigate();
        const funnel = row.original;
        const displayName = funnel.name || t('funnelsTable.columns.untitledFunnel');
        const description = funnel.description;
        const funnelLink = storeRoutes.STOREFRONT_EDITOR(funnel.id);
        return (
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 rounded-lg p-2">
              <GitBranch className="text-primary h-4 w-4" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1">
              <Link to={funnelLink} className="truncate text-sm font-medium leading-none hover:underline">
                {displayName}
              </Link>
              {description && (
                <p className="text-muted-foreground truncate text-xs leading-none">{description}</p>
              )}
            </div>
          </div>
        );
      },
      meta: {
        label: t('funnelsTable.columns.funnelName'),
        placeholder: t('funnelsTable.columns.searchFunnels'),
        variant: 'text',
        icon: GitBranch,
      },
      enableColumnFilter: true,
      size: 250,
    }),

    // 3. Status
    columnHelper.accessor('status', {
      id: 'funnels.status' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.status')} />
      ),
      cell: ({ getValue }) => {
        const status = getValue() as string;
        const statusVariants = {
          active: 'green',
          draft: 'gray',
          paused: 'yellow',
        } as const;

        const variant =
          status in statusVariants ? statusVariants[status as keyof typeof statusVariants] : 'gray';

        const translated = t(`funnelsTable.status.${status}`, { defaultValue: status });

        return (
          <Badge variant={variant} className="capitalize">
            {translated}
          </Badge>
        );
      },
      meta: {
        label: t('funnelsTable.columns.status'),
        variant: 'multiSelect',
        options: [
          { label: t('funnelsTable.status.active'), value: 'active' },
          { label: t('funnelsTable.status.draft'), value: 'draft' },
          { label: t('funnelsTable.status.paused'), value: 'paused' },
        ],
        icon: CircleDashed,
      },
      enableColumnFilter: true,
      size: 100,
    }),

    // 4. Is Default
    columnHelper.accessor('isDefault', {
      id: 'funnels.isDefault' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.default')} />
      ),
      cell: ({ getValue }) => {
        const isDefault = getValue();
        return isDefault ? (
          <Badge variant="blue">{t('funnelsTable.columns.default')}</Badge>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        );
      },
      meta: {
        label: t('funnelsTable.columns.default'),
      },
      enableSorting: true,
      size: 100,
    }),

    // 5. Steps Count
    columnHelper.accessor('stepsCount', {
      id: 'funnels.stepsCount' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.steps')} />
      ),
      cell: ({ getValue, row }) => {
        const count = getValue() || 0;
        const stepTypes = row.original.stepTypes || [];
        // Get unique step types to save space
        const uniqueStepTypes = [...new Set(stepTypes)];
        
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium tabular-nums">{count}</span>
            {uniqueStepTypes.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {uniqueStepTypes.map((type, idx) => (
                  <StepTypeBadge key={idx} type={type} />
                ))}
              </div>
            )}
          </div>
        );
      },
      meta: {
        label: t('funnelsTable.columns.stepsCount'),
        variant: 'number',
        icon: GitBranch,
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 200,
    }),

    // NOTE: Progression rate is a per-step metric, not a funnel-level metric
    // Removed for now - could add funnel-level aggregated analytics later

    // 6. Created At
    columnHelper.accessor<'createdAt', string>('createdAt', {
      id: 'funnels.createdAt' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.created')} />
      ),
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return <span className="text-muted-foreground">—</span>;

        return (
          <div className="flex flex-col gap-0.5 py-1">
            <div className="text-sm leading-none">{formatDate(date, 'locale')}</div>
          </div>
        );
      },
      meta: {
        label: t('funnelsTable.columns.createdDate'),
        variant: 'dateRange',
        icon: CalendarIcon,
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 160,
    }),

    // 7. Updated At
    columnHelper.accessor<'updatedAt', string>('updatedAt', {
      id: 'funnels.updatedAt' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.lastUpdated')} />
      ),
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return <span className="text-muted-foreground">—</span>;

        return (
          <div className="flex flex-col gap-0.5 py-1">
            <div className="text-sm leading-none">{formatDate(date, 'locale')}</div>
          </div>
        );
      },
      meta: {
        label: t('funnelsTable.columns.updatedDate'),
        variant: 'dateRange',
        icon: CalendarIcon,
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 160,
    }),

    // 8. Funnel ID (moved to end, copyable) — matches convention in
    // products, orders, customers, payments, email templates tables across the app.
    columnHelper.accessor('id', {
      id: 'funnels.id' satisfies FunnelTableColumnId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('funnelsTable.columns.funnelId')} />
      ),
      cell: ({ getValue }) => {
        const fullId = getValue();
        const displayValue = `${fullId.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('funnelsTable.columns.funnelId')}
            value={displayValue}
            copyValue={fullId}
          />
        );
      },
      meta: {
        label: t('funnelsTable.columns.funnelId'),
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
    }),

    // 9. Actions
    columnHelper.display({
      id: 'actions' satisfies FunnelTableColumnId,
      cell: ({ row }) => {
        const funnel = row.original;
        const funnelLink = `../storefront/${funnel.id}`;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('funnelsTable.actions.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuItem asChild>
                <Link to={funnelLink}>{t('funnelsTable.actions.viewDetails')}</Link>
              </DropdownMenuItem>
              {onEditFunnel && (
                <DropdownMenuItem onClick={() => onEditFunnel(funnel.id)}>
                  {t('funnelsTable.actions.editFunnel')}
                </DropdownMenuItem>
              )}
              {onExportFunnel && (
                <DropdownMenuItem onClick={() => onExportFunnel(funnel.id)}>
                  {t('funnelsTable.actions.exportBundle')}
                </DropdownMenuItem>
              )}
              {onSetAsDefault && !funnel.isDefault && (
                <DropdownMenuItem onClick={() => onSetAsDefault(funnel.id)}>
                  {t('funnelsTable.actions.setAsDefault')}
                </DropdownMenuItem>
              )}
              {onDeleteFunnel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDeleteFunnel(funnel.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    {t('funnelsTable.actions.deleteFunnel')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    }),
  ] as ColumnDef<Funnel>[];
}
